import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Banner from 'src/components/common/Banner';
import { Card } from 'src/components/common/Card';
import Cooladata from 'src/components/common/Cooladata';
import StorycardsCredits from 'src/components/common/StorycardsCredits';
import { QUESTIONS_ROUTE } from 'src/constants/routes';
import { useAppSelector } from 'src/hooks/store';
import { AppDispatch } from 'src/store';
import { clearResults, clearVersionMismatch } from 'src/store/data';
import { getDuels } from 'src/store/data/actions';
import { selectIntroImages, selectParties, selectVersion } from 'src/store/data/selectors';
import { Party } from 'src/types/data-types';
import { analytics } from 'src/utils/analytics';
import { ensureGoogleAudiencePixel } from 'src/utils/google-audience-pixel';
import { clientSession } from 'src/utils/session-storage';

import Intro from './Intro';
import css from './Main.module.scss';

export default function Main() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const version = useAppSelector(selectVersion);
  const parties = useAppSelector(selectParties);
  const introImages = useAppSelector(selectIntroImages);
  const [bluePartyId, setBluePartyId] = useState<null | string>(null);
  const [redPartyId, setRedPartyId] = useState<null | string>(null);

  useEffect(() => {
    dispatch(clearVersionMismatch());
  }, [dispatch]);

  useEffect(() => {
    ensureGoogleAudiencePixel();
  }, []);

  useEffect(() => {
    if (version) {
      return;
    }

    setBluePartyId(null);
    setRedPartyId(null);
  }, [version]);

  const onPartySelect = (id: string) => {
    if (bluePartyId === id) {
      setBluePartyId(null);
      return;
    }

    if (redPartyId === id) {
      setRedPartyId(null);
      return;
    }

    if (!bluePartyId) {
      setBluePartyId(id);
    } else {
      setRedPartyId(id);
    }
  };

  const onContinueClick = () => {
    if (!bluePartyId || !redPartyId || !version || bluePartyId === redPartyId) {
      return;
    }

    const partyA = parties.find((p: Party) => p.id === bluePartyId);
    const partyB = parties.find((p: Party) => p.id === redPartyId);
    analytics.gtag.event('בחירת מפלגות', {
      Party_Name_1: partyA.name,
      Party_Name_2: partyB.name,
    });
    dispatch(clearVersionMismatch());
    dispatch(clearResults());
    clientSession.create(version, bluePartyId, redPartyId);
    void dispatch(getDuels());
    setTimeout(() => {
      if (!clientSession.isValid(clientSession.get())) {
        return;
      }

      navigate(QUESTIONS_ROUTE);
    }, 350);
  };

  return (
    <div className={css.main}>
      <div id="mainBanner">
        <Banner />
      </div>
      <Intro portraits={introImages} />
      <div className={css.dashedBorder} />
      <div className={css.list}>
        <div className={css.scrollable}>
          {!isEmpty(parties) &&
            parties.map((card: Party) => (
              <Card
                key={card.id}
                data={card}
                state={
                  (bluePartyId === card.id && 'blue') ||
                  (redPartyId === card.id && 'red') ||
                  (bluePartyId && redPartyId && 'disabled') ||
                  'default'
                }
                onSelect={onPartySelect}
              />
            ))}
        </div>

        <button
          type="button"
          className={cn(css.button, bluePartyId && redPartyId && css.active)}
          onClick={onContinueClick}
        >
          המשך
        </button>
      </div>
      <Cooladata pageId="HP" />
      <StorycardsCredits />
    </div>
  );
}
